import React, { useState } from 'react';
import PropTypes from 'prop-types';
import DialogModal from 'tradera-components/dialog/dialog-modal';
import EmailIcon from '@tradera/blueprint/theme/icons2/network-email.svg?jsx';
import FacebookIcon from '@tradera/blueprint/theme/icons2/network-fb.svg?jsx';
import PinterestIcon from '@tradera/blueprint/theme/icons2/network-pin.svg?jsx';
import CloseIcon from '@tradera/blueprint/theme/icons2/close.svg?jsx';
import { useTranslator } from 'tradera-lang/translate';
import CopyToClipboardUtil from 'utils/copy-to-clipboard';
import {
    getEmailShareLink,
    getFacebookShareLink,
    getPinterestShareLink
} from 'tradera-utils/share-link';

const ShareModal = ({
    onDismiss,
    shareTitle,
    shareImageUrl,
    translationKeys,
    gaCategory
}) => {
    const { t } = useTranslator();
    const [showCopiedNotification, setShowCopiedNotification] = useState(false);
    const copyLink = () => {
        CopyToClipboardUtil.copy(linkRef);
        setShowCopiedNotification(true);
        setTimeout(() => setShowCopiedNotification(false), 1000);
    };

    let linkRef = null;

    const setLinkRef = element => {
        if (element && !linkRef) {
            linkRef = element;
        }
    };

    const {
        shareHeader,
        shareMailSubject,
        shareMailBody,
        sharePinterestDescription,
        shareLinkText,
        shareCopy = 'shareModal_copy',
        shareCopied = 'shareModal_copied',
        shareClose = 'shareModal_close'
    } = translationKeys;

    return (
        <DialogModal
            className="flex-column position-relative"
            style={{ width: '30rem', maxWidth: '100%', margin: '0 auto' }}
            mobileFullscreen={true}
            onDismiss={onDismiss}>
            <div className="container p-3">
                <h2 className="heading-berlin text-center text-styled">
                    {t(shareHeader)}
                </h2>
                <button
                    className="btn"
                    onClick={e => {
                        e.preventDefault();
                        onDismiss();
                    }}
                    style={{
                        position: 'absolute',
                        top: '1rem',
                        right: '0.5rem'
                    }}>
                    <CloseIcon className="icon" />
                </button>

                <div className="d-flex justify-content-between p-5 border-bottom">
                    <a
                        href={getEmailShareLink({
                            subject: t(shareMailSubject, {
                                title: shareTitle
                            }),
                            body: t(shareMailBody, {
                                url: location.href
                            })
                        })}
                        rel="nofollow"
                        data-ga-category={gaCategory}
                        data-ga-action="Navigation"
                        data-ga-label="Sociala medier - Mail">
                        <EmailIcon className="icon icon-xl icon-dark" />
                    </a>
                    <a
                        href={getFacebookShareLink({
                            url: location.href
                        })}
                        rel="nofollow"
                        data-ga-category={gaCategory}
                        data-ga-action="Navigation"
                        data-ga-label="Sociala medier - Facebook">
                        <FacebookIcon className="icon icon-xl icon-dark" />
                    </a>
                    <a
                        href={getPinterestShareLink({
                            url: location.href,
                            description: t(sharePinterestDescription, {
                                title: shareTitle
                            }),
                            imageUrl: shareImageUrl
                        })}
                        rel="nofollow"
                        data-ga-category={gaCategory}
                        data-ga-action="Navigation"
                        data-ga-label="Sociala medier - Pinterest">
                        <PinterestIcon className="icon icon-xl icon-dark" />
                    </a>
                </div>
                <div className="row align-items-center py-4 border-bottom mx-0">
                    <div className="col-12 col-md-9">
                        <p className="text-inter-heavy mb-1">
                            {t(shareLinkText)}
                        </p>
                        <p className="text-break" ref={setLinkRef}>
                            {window.location.href}
                        </p>
                    </div>
                    <div className="col-12 col-md-3">
                        <button
                            className="btn btn-outline-primary"
                            type="button"
                            onClick={e => {
                                e.preventDefault();
                                copyLink();
                            }}>
                            {t(shareCopy)}
                        </button>
                        <div
                            className={`position-fixed bg-dark text-white p-1 mt-1 ${
                                showCopiedNotification ? 'd-block' : 'd-none'
                            }`}>
                            {t(shareCopied)}
                        </div>
                    </div>
                </div>
                <div className="d-flex justify-content-center mt-4">
                    <button
                        className="btn btn-fluid btn-primary"
                        type="button"
                        onClick={e => {
                            e.preventDefault();
                            onDismiss();
                        }}>
                        {t(shareClose)}
                    </button>
                </div>
            </div>
        </DialogModal>
    );
};

ShareModal.propTypes = {
    onDismiss: PropTypes.func.isRequired,
    shareTitle: PropTypes.string.isRequired,
    translationKeys: PropTypes.shape({
        shareHeader: PropTypes.string.isRequired,
        shareMailSubject: PropTypes.string.isRequired,
        shareMailBody: PropTypes.string.isRequired,
        sharePinterestDescription: PropTypes.string.isRequired,
        shareLinkText: PropTypes.string.isRequired,
        shareCopy: PropTypes.string,
        shareCopied: PropTypes.string,
        shareClose: PropTypes.string
    })
};

export { ShareModal };
